import { useState } from 'react'
import { useLocation } from 'wouter'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FloatingInput } from '@/components/ui/floating-input'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'
import { signOut } from 'firebase/auth'
import { auth } from '@/constants'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from '@/components/ui/alert-dialog'

export default function DeleteAccountPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { uid, disconnectWallet, isConnected, setUserData } = useUser()

	const [isDeleting, setIsDeleting] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [deletePassword, setDeletePassword] = useState('')
	const [deletePasswordError, setDeletePasswordError] = useState('')

	const handleDeleteAccount = async () => {
		if (!deletePassword.trim()) {
			setDeletePasswordError('Please enter your password to confirm')
			return
		}

		setDeletePasswordError('')
		setIsDeleting(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'DELETE',
				body: JSON.stringify({ password: deletePassword }),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to delete account')
			}

			document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
			await signOut(auth)
			setUserData(null)

			if (isConnected) {
				await disconnectWallet()
			}

			toast({
				title: 'Account Deleted',
				description: 'Your account has been successfully deleted.'
			})

			navigate('/login')
		} catch (error) {
			toast({
				title: 'Deletion Failed',
				description: error instanceof Error ? error.message : 'Failed to delete account',
				variant: 'destructive'
			})
		} finally {
			setIsDeleting(false)
			setShowConfirmModal(false)
			setDeletePassword('')
		}
	}

	const closeConfirm = () => {
		setShowConfirmModal(false)
		setDeletePassword('')
		setDeletePasswordError('')
	}

	return (
		<div className="h-[100dvh] bg-[#18181B] text-white flex flex-col">
			<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				{/* Header */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/account')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Delete Account
					</h1>
				</div>

				{/* Center content */}
				<div className="flex-1 flex items-center justify-center px-5 pb-20">
					<div className="w-full max-w-sm space-y-5 text-center">
					<p className="font-figtree font-normal leading-[20px] text-[14px] text-[#BABABB]">
						Deleting your account is permanent and cannot be undone. Your data will be removed and you will lose access to all services.
					</p>

					<Button
						onClick={() => setShowConfirmModal(true)}
						className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
					>
						Delete My Account
					</Button>
				</div>
				</div>
			</div>

			<AlertDialog open={showConfirmModal} onOpenChange={closeConfirm}>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							Are you sure you want to delete your account?
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							This action cannot be undone. Your account will be deactivated and your data will be removed.
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="my-2">
						<FloatingInput
							label="Password"
							type="password"
							autoFocus
							value={deletePassword}
							onChange={(e) => {
								setDeletePassword(e.target.value)
								setDeletePasswordError('')
							}}
						/>
						{deletePasswordError && (
							<p className="text-sm text-[#EF4444] mt-2 text-center font-figtree">{deletePasswordError}</p>
						)}
					</div>

					<AlertDialogFooter className="flex flex-col gap-3 !sm:flex-col !sm:justify-center !sm:space-x-0 items-center">
						<AlertDialogAction
							onClick={(e) => {
								e.preventDefault()
								handleDeleteAccount()
							}}
							disabled={isDeleting}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{isDeleting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Deleting...
								</>
							) : (
								'Delete Account'
							)}
						</AlertDialogAction>
						<AlertDialogCancel
							onClick={closeConfirm}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
